import { useState, useEffect, useRef, useMemo } from "react";
import {
  createGoal,
  updateGoal,
  deleteGoal,
  updateGoalProgress,
  getGoalsByUser,
  useQuery,
  useAction,
} from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import type { TradingGoal } from "wasp/entities";
import { Plus, Edit, Trash2, Target, AlertCircle, RefreshCw, Sparkles } from "lucide-react";
import { PaymentPlanId, SubscriptionStatus } from "../payment/plans";
import PlanSelectionModal from "../dashboard/PlanSelectionModal";
import { Button } from "../client/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../client/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../client/components/ui/dialog";
import { Input } from "../client/components/ui/input";
import { Label } from "../client/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../client/components/ui/select";
import { Textarea } from "../client/components/ui/textarea";
import { Progress } from "../client/components/ui/progress";
import { toast } from "../client/hooks/use-toast";
import useTranslation from "../client/hooks/useTranslation";
import { BeamButton } from "../client/components/ui/beam-button";
import { motion } from "framer-motion";
import { trackGoalCreated } from '../client/utils/posthog';
import GoalHeatmap from "./GoalHeatmap";
import type { GoalWithHistory } from "./operations";

// Date helpers
const formatDate = (date: Date): string => {
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${year}-${month}-${day}`;
};

const differenceInDays = (date1: Date, date2: Date): number => {
  const diffTime = date1.getTime() - date2.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

type RecurrenceType = "none" | "daily" | "weekly" | "monthly";

interface GoalFormData {
  title: string;
  description: string;
  type: "pnl" | "win_rate" | "trades_count" | "custom";
  targetValue: string;
  deadline: string;
  recurrence: RecurrenceType;
}

const initialFormData: GoalFormData = {
  title: "",
  description: "",
  type: "pnl",
  targetValue: "",
  deadline: "",
  recurrence: "none",
};

export default function TradingGoalsPage({ sectionTitle }: { sectionTitle?: string } = {}) {
  const t = useTranslation();
  const { data: user } = useAuth();
  const [showPlanModal, setShowPlanModal] = useState(false);
  const isFreePlan = useMemo(() => {
    if (!user) return false;
    if (!user.subscriptionPlan || user.subscriptionPlan === PaymentPlanId.Free) return true;
    if (!user.subscriptionStatus || user.subscriptionStatus === SubscriptionStatus.Deleted) return true;
    return false;
  }, [user]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<TradingGoal | null>(null);
  const [formData, setFormData] = useState<GoalFormData>(initialFormData);

  const createGoalFn = useAction(createGoal);
  const updateGoalFn = useAction(updateGoal);
  const deleteGoalFn = useAction(deleteGoal);
  const updateProgressFn = useAction(updateGoalProgress);

  const { data: goals, isLoading, refetch } = useQuery(getGoalsByUser);
  const hasUpdatedProgress = useRef(false);

  // Update progress only once when goals are first loaded
  useEffect(() => {
    if (goals && goals.length > 0 && !hasUpdatedProgress.current) {
      const hasGoalsToUpdate = goals.some(
        (g: GoalWithHistory) =>
          !g.isCompleted &&
          !(g.recurrence !== "none" && g.deadline && new Date(g.deadline) < new Date())
      );
      if (hasGoalsToUpdate) {
        hasUpdatedProgress.current = true;
        updateProgressFn(undefined)
          .then(() => {
            setTimeout(() => {
              refetch();
            }, 100);
          })
          .catch((err) => {
            console.error("Failed to update goal progress:", err);
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goals?.length]);

  const handleCreate = async () => {
    if (!formData.title || formData.title.trim() === "") {
      toast({
        title: t.common.error,
        description: t.validation.fieldRequired.replace("{field}", t.goals.goalTitle),
        variant: "destructive",
      });
      return;
    }
    if (!formData.targetValue || isNaN(parseFloat(formData.targetValue))) {
      toast({
        title: t.common.error,
        description: t.validation.fieldRequired.replace("{field}", t.goals.targetValue),
        variant: "destructive",
      });
      return;
    }

    try {
      await createGoalFn({
        title: formData.title,
        description: formData.description || null,
        type: formData.type,
        targetValue: parseFloat(formData.targetValue),
        deadline: formData.recurrence === "none" && formData.deadline
          ? new Date(formData.deadline)
          : null,
        recurrence: formData.recurrence,
      });
      trackGoalCreated();
      toast({
        title: t.goals.goalCreated,
        description: t.goals.goalCreatedDescription,
      });
      setIsCreateDialogOpen(false);
      setFormData(initialFormData);
      refetch();
    } catch (error: any) {
      toast({
        title: t.common.error,
        description: error.message || t.goals.cannotCreate,
        variant: "destructive",
      });
    }
  };

  const handleEdit = (goal: TradingGoal) => {
    setEditingGoal(goal);
    const recurrence = (goal as GoalWithHistory).recurrence as RecurrenceType ?? "none";
    setFormData({
      title: goal.title,
      description: goal.description || "",
      type: goal.type as "pnl" | "win_rate" | "trades_count" | "custom",
      targetValue: goal.targetValue.toString(),
      deadline: recurrence === "none" && goal.deadline
        ? formatDate(new Date(goal.deadline))
        : "",
      recurrence,
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingGoal) return;

    if (!formData.title || formData.title.trim() === "") {
      toast({
        title: t.common.error,
        description: t.validation.fieldRequired.replace("{field}", t.goals.goalTitle),
        variant: "destructive",
      });
      return;
    }
    if (!formData.targetValue || isNaN(parseFloat(formData.targetValue))) {
      toast({
        title: t.common.error,
        description: t.validation.fieldRequired.replace("{field}", t.goals.targetValue),
        variant: "destructive",
      });
      return;
    }

    try {
      await updateGoalFn({
        id: editingGoal.id,
        title: formData.title,
        description: formData.description || null,
        type: formData.type,
        targetValue: parseFloat(formData.targetValue),
        deadline: formData.recurrence === "none" && formData.deadline
          ? new Date(formData.deadline)
          : null,
        recurrence: formData.recurrence,
      });
      toast({
        title: t.goals.goalUpdated,
        description: t.goals.goalUpdatedDescription,
      });
      setIsEditDialogOpen(false);
      setEditingGoal(null);
      setFormData(initialFormData);
      refetch();
    } catch (error: any) {
      toast({
        title: t.common.error,
        description: error.message || t.goals.cannotUpdate,
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (goal: TradingGoal) => {
    if (!confirm(t.goals.confirmDelete)) return;

    try {
      await deleteGoalFn({ id: goal.id });
      toast({
        title: t.goals.goalDeleted,
        description: t.goals.goalDeletedDescription,
      });
      refetch();
    } catch (error: any) {
      toast({
        title: t.common.error,
        description: error.message || t.goals.cannotDelete,
        variant: "destructive",
      });
    }
  };

  const getProgressPercentage = (goal: TradingGoal) => {
    if (goal.targetValue === 0) return 0;
    return Math.min((goal.currentValue / goal.targetValue) * 100, 100);
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "pnl":
        return `${t.goals.pnl} ($)`;
      case "win_rate":
        return `${t.goals.winRate} (%)`;
      case "trades_count":
        return t.goals.tradesCount;
      default:
        return t.goals.custom;
    }
  };

  if (isLoading) {
    return (
      <div className="pt-6">
        <div className="text-center">{t.goals.loading}</div>
      </div>
    );
  }

  const activeGoals = goals?.filter((g) => !g.isCompleted) || [];
  const completedGoals = goals?.filter((g) => g.isCompleted) || [];

  return (
    <div className="pt-6">
      <motion.div
        className="mb-4 flex items-center justify-between"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
      >
        {sectionTitle && (
          <h2 className="text-xl font-semibold text-muted-foreground uppercase tracking-wider">
            {sectionTitle}
          </h2>
        )}
        <BeamButton size="default" onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t.goals.newGoal}
        </BeamButton>
      </motion.div>

      {isFreePlan && goals && goals.length >= 1 && (
        <Card className="mb-6 border-primary/50 bg-gradient-to-r from-primary/5 via-background to-primary/5">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="rounded-full bg-primary/10 p-3">
                  <Sparkles className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{t.goals.limitReached}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{t.goals.limitReachedDescription}</p>
                </div>
              </div>
              <BeamButton onClick={() => setShowPlanModal(true)}>
                <Sparkles className="h-4 w-4 mr-2" />
                {t.dashboard.upgradeButton}
              </BeamButton>
            </div>
          </CardContent>
        </Card>
      )}

      {activeGoals.length > 0 && (
        <div className="mb-6">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">{t.dashboard.activeGoals}</h3>
          <div className="grid grid-cols-1 gap-4">
            {activeGoals.map((goal) => (
              <GoalCard
                key={goal.id}
                goal={goal}
                onEdit={handleEdit}
                onDelete={handleDelete}
                getProgressPercentage={getProgressPercentage}
                getTypeLabel={getTypeLabel}
              />
            ))}
          </div>
        </div>
      )}

      {completedGoals.length > 0 && (
        <div className="mb-6">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">{t.goals.completed}</h3>
          <div className="grid grid-cols-1 gap-4">
            {completedGoals.map((goal) => (
              <GoalCard
                key={goal.id}
                goal={goal}
                onEdit={handleEdit}
                onDelete={handleDelete}
                getProgressPercentage={getProgressPercentage}
                getTypeLabel={getTypeLabel}
              />
            ))}
          </div>
        </div>
      )}

      {goals && goals.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-muted-foreground mb-4">
              {t.goals.noGoals}
            </p>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t.goals.createFirst}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.goals.newGoal}</DialogTitle>
            <DialogDescription>
              {t.goals.subtitle}
            </DialogDescription>
          </DialogHeader>
          <GoalForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleCreate}
            onCancel={() => {
              setIsCreateDialogOpen(false);
              setFormData(initialFormData);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.goals.editGoal}</DialogTitle>
            <DialogDescription>
              {t.goals.subtitle}
            </DialogDescription>
          </DialogHeader>
          <GoalForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleUpdate}
            onCancel={() => {
              setIsEditDialogOpen(false);
              setEditingGoal(null);
              setFormData(initialFormData);
            }}
          />
        </DialogContent>
      </Dialog>
      <PlanSelectionModal open={showPlanModal} onOpenChange={setShowPlanModal} />
    </div>
  );
}

function GoalCard({
  goal,
  onEdit,
  onDelete,
  getProgressPercentage,
  getTypeLabel,
}: {
  goal: GoalWithHistory;
  onEdit: (goal: TradingGoal) => void;
  onDelete: (goal: TradingGoal) => void;
  getProgressPercentage: (goal: TradingGoal) => number;
  getTypeLabel: (type: string) => string;
}) {
  const t = useTranslation();
  const progress = getProgressPercentage(goal);
  const recurrence = goal.recurrence as RecurrenceType ?? "none";
  const daysUntilDeadline = goal.deadline
    ? differenceInDays(new Date(goal.deadline), new Date())
    : null;
  const isDeadlineApproaching =
    daysUntilDeadline !== null && daysUntilDeadline <= 7 && daysUntilDeadline >= 0;
  const isDeadlinePassed =
    daysUntilDeadline !== null &&
    daysUntilDeadline < 0 &&
    !goal.isCompleted &&
    recurrence === "none";

  const recurrenceBadgeLabel =
    recurrence === "daily"
      ? t.goals.badgeDaily
      : recurrence === "weekly"
      ? t.goals.badgeWeekly
      : recurrence === "monthly"
      ? t.goals.badgeMonthly
      : null;

  return (
    <Card className={goal.isCompleted ? "opacity-75" : ""}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 shrink-0" />
              <span>{goal.title}</span>
              {recurrenceBadgeLabel && (
                <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-primary/10 text-primary whitespace-nowrap">
                  <RefreshCw className="inline h-3 w-3 mr-1" />
                  {recurrenceBadgeLabel}
                </span>
              )}
            </CardTitle>
            {goal.description && (
              <p className="text-sm text-muted-foreground mt-1">
                {goal.description}
              </p>
            )}
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="ghost" size="sm" onClick={() => onEdit(goal)}>
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(goal)}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">
                {getTypeLabel(goal.type)}
              </span>
              <span className="text-sm text-muted-foreground">
                {goal.currentValue.toFixed(2)} / {goal.targetValue.toFixed(2)}
              </span>
            </div>
            <Progress value={progress} className="h-2" />
            <div className="mt-1 text-xs text-muted-foreground text-right">
              {progress.toFixed(1)}%
            </div>
          </div>

          {goal.deadline && (
            <div className="flex items-center gap-2 text-sm">
              {isDeadlineApproaching && (
                <AlertCircle className="h-4 w-4 text-warning shrink-0" />
              )}
              {isDeadlinePassed && (
                <AlertCircle className="h-4 w-4 text-pnlNegative shrink-0" />
              )}
              <span
                className={
                  isDeadlineApproaching
                    ? "text-warning font-medium"
                    : isDeadlinePassed
                    ? "text-pnlNegative font-medium"
                    : "text-muted-foreground"
                }
              >
                {t.goals.deadline}: {(() => {
                  const d = new Date(goal.deadline);
                  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
                })()}
                {daysUntilDeadline !== null && recurrence === "none" && (
                  <span className="ml-2">
                    ({daysUntilDeadline >= 0
                      ? `${daysUntilDeadline} ${t.goals.daysRemaining}`
                      : `${Math.abs(daysUntilDeadline)} ${t.goals.daysOverdue}`})
                  </span>
                )}
                {daysUntilDeadline !== null && recurrence !== "none" && daysUntilDeadline >= 0 && (
                  <span className="ml-2">
                    ({daysUntilDeadline} {t.goals.daysRemaining})
                  </span>
                )}
              </span>
            </div>
          )}

          {goal.isCompleted && goal.completedAt && (
            <div className="text-sm text-pnlPositive font-medium">
              ✓ {t.goals.completed} {(() => {
                  const d = new Date(goal.completedAt!);
                  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
                })()}
            </div>
          )}

          {recurrence !== "none" && goal.history && (
            <GoalHeatmap
              history={goal.history}
              recurrence={recurrence as "daily" | "weekly" | "monthly"}
              targetValue={goal.targetValue}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function GoalForm({
  formData,
  setFormData,
  onSubmit,
  onCancel,
}: {
  formData: GoalFormData;
  setFormData: (data: GoalFormData) => void;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const t = useTranslation();
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="title">{t.goals.goalTitle} *</Label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) =>
            setFormData({ ...formData, title: e.target.value })
          }
          required
        />
      </div>

      <div>
        <Label htmlFor="description">{t.goals.description}</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
          rows={2}
        />
      </div>

      <div>
        <Label htmlFor="type">{t.goals.type} *</Label>
        <Select
          value={formData.type}
          onValueChange={(value: "pnl" | "win_rate" | "trades_count" | "custom") =>
            setFormData({ ...formData, type: value })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pnl">{t.goals.pnl} ($)</SelectItem>
            <SelectItem value="win_rate">{t.goals.winRate} (%)</SelectItem>
            <SelectItem value="trades_count">{t.goals.tradesCount}</SelectItem>
            <SelectItem value="custom">{t.goals.custom}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="targetValue">{t.goals.targetValue} *</Label>
        <Input
          id="targetValue"
          type="number"
          step="0.01"
          value={formData.targetValue}
          onChange={(e) =>
            setFormData({ ...formData, targetValue: e.target.value })
          }
          required
        />
      </div>

      <div>
        <Label htmlFor="recurrence">{t.goals.recurrence}</Label>
        <Select
          value={formData.recurrence}
          onValueChange={(value: RecurrenceType) =>
            setFormData({ ...formData, recurrence: value, deadline: "" })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">{t.goals.recurrenceNone}</SelectItem>
            <SelectItem value="daily">{t.goals.recurrenceDaily}</SelectItem>
            <SelectItem value="weekly">{t.goals.recurrenceWeekly}</SelectItem>
            <SelectItem value="monthly">{t.goals.recurrenceMonthly}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {formData.recurrence === "none" && (
        <div>
          <Label htmlFor="deadline">{t.goals.deadline}</Label>
          <Input
            id="deadline"
            type="date"
            value={formData.deadline}
            onChange={(e) =>
              setFormData({ ...formData, deadline: e.target.value })
            }
          />
        </div>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          {t.goals.cancel}
        </Button>
        <Button onClick={onSubmit}>{t.goals.save}</Button>
      </DialogFooter>
    </div>
  );
}
